<template>
  <div class="upgrade-window">
    <!-- Energy indicator -->
    <div class="energy-indicator">
      ⚡ {{ formatDisplay(energyCurrent) }} / {{ formatDisplay(energyMax) }}
    </div>
    
    <!-- Boost button -->
    <div class="boost-button-container">
      <BoostButton 
        :is-boost-active="boostActive"
        :boost-expire-at="boostExpireAt"
        :boost-cooldown-end="boostCooldownEnd"
        @activate-boost="$emit('activate-boost')"
      />
    </div>
    
    <!-- Energy bar -->
    <div class="energy-bar-container">
      <div class="energy-bar">
        <div class="energy-fill" :style="{ width: energyPercentage + '%' }"></div>
      </div>
    </div>
    
    <!-- Upgrade buttons -->
    <div class="upgrade-buttons">
      <div class="tooltip-wrapper">
        <button 
          class="upgrade-button" 
          @click="$emit('upgrade-click')"
          :disabled="clickUpgradeCost > balance"
        >
          <div class="button-content">
            <img :src="klikImage" alt="Click Upgrade" class="upgrade-icon" />
            <div class="button-cost-container">
              <img :src="coinImage" alt="Coin" class="coin-icon" />
              <div class="button-cost">{{ formatDisplay(clickUpgradeCost) }}</div>
            </div>
          </div>
        </button>
      </div>
      
      <div class="tooltip-wrapper">
        <button 
          class="upgrade-button" 
          @click="$emit('upgrade-energy')"
          :disabled="energyUpgradeCost > balance"
        >
          <div class="button-content">
            <img :src="energyImage" alt="Energy Upgrade" class="upgrade-icon" />
            <div class="button-cost-container">
              <img :src="coinImage" alt="Coin" class="coin-icon" />
              <div class="button-cost">{{ formatDisplay(energyUpgradeCost) }}</div>
            </div>
          </div>
        </button>
      </div>
      
      <div class="tooltip-wrapper">
        <button 
          class="upgrade-button" 
          @click="$emit('upgrade-auto-tap')"
          :disabled="autoTapCost > balance || autoTapLevel >= maxAutoTapLevel"
        >
          <div class="button-content">
            <img :src="robotImage" alt="Auto Tap" class="upgrade-icon" />
            <div class="button-level">{{ autoTapLevel }}/{{ maxAutoTapLevel }}</div>
            <div class="button-cost-container">
              <img :src="coinImage" alt="Coin" class="coin-icon" />
              <div class="button-cost">{{ formatDisplay(autoTapCost) }}</div>
            </div>
          </div>
        </button>
      </div>
      
      <div class="tooltip-wrapper">
        <button 
          class="upgrade-button" 
          @click="$emit('refill-energy')"
          :disabled="energyRefillCost > balance || energyCurrent >= energyMax"
        >
          <div class="button-content">
            <img :src="shkalaImage" alt="Energy Refill" class="upgrade-icon" />
            <div class="button-text">Пополнить</div>
            <div class="button-cost-container">
              <img :src="coinImage" alt="Coin" class="coin-icon" />
              <div class="button-cost">{{ formatDisplay(energyRefillCost) }}</div>
            </div>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import BoostButton from './BoostButton.vue';

export default {
  name: 'UpgradeWindow',
  components: {
    BoostButton
  },
  props: {
    balance: {
      type: Number,
      required: true
    },
    energyCurrent: {
      type: Number,
      required: true
    },
    energyMax: {
      type: Number,
      required: true
    },
    clickUpgradeCost: {
      type: Number,
      required: true
    },
    energyUpgradeCost: {
      type: Number,
      required: true
    },
    autoTapCost: {
      type: Number,
      required: true
    },
    energyRefillCost: {
      type: Number,
      default: 2500
    },
    autoTapLevel: {
      type: Number,
      required: true
    },
    maxAutoTapLevel: {
      type: Number,
      default: 8
    },
    boostActive: {
      type: Boolean,
      default: false
    },
    boostExpireAt: {
      type: Number,
      default: null
    },
    boostCooldownEnd: {
      type: Number,
      default: null
    }
  },
  emits: ['upgrade-click', 'upgrade-energy', 'upgrade-auto-tap', 'refill-energy', 'activate-boost'],
  setup(props) {
    // Import images
    const coinImage = new URL('../assets/Moneta.png', import.meta.url).href;
    const klikImage = new URL('../assets/klik.png', import.meta.url).href;
    const energyImage = new URL('../assets/energy.png', import.meta.url).href;
    const robotImage = new URL('../assets/robot.PNG', import.meta.url).href;
    const shkalaImage = new URL('../assets/shkala.png', import.meta.url).href;
    
    const energyPercentage = computed(() => {
      return (props.energyCurrent / props.energyMax) * 100;
    });
    
    const formatDisplay = (num) => {
      if (typeof num !== 'number') num = Number(num) || 0;
      // Format as whole number without decimals
      return Math.floor(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };
    
    return {
      coinImage,
      klikImage,
      energyImage,
      robotImage,
      shkalaImage,
      energyPercentage,
      formatDisplay
    };
  }
};
</script>

<style scoped>
.upgrade-window {
  position: fixed;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 355px; /* Увеличенный размер на 5px */
  padding: 12px; /* Увеличено на 2px */
  background: transparent;
  border-radius: 15px;
  box-shadow: none;
  backdrop-filter: none;
}

.energy-indicator {
  font-size: 12px;
  color: #f39c12; /* Orange-yellow color */
  margin-bottom: 4px; /* Уменьшен отступ для приближения к шкале */
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  padding: 4px 10px;
  border-radius: 15px;
  width: fit-content;
  border: none;
  /* Убран margin-left: auto; чтобы элемент был слева */
}

.boost-button-container {
  position: absolute;
  top: -20px; /* Поднят на 2 см (примерно 20px) */
  right: -30px; /* Сдвинут правее */
  background: transparent;
  border-radius: 8px;
  padding: 5px; /* Увеличен на 2px */
  border: none;
}

.energy-bar-container {
  margin: 8px 0; /* Уменьшен отступ для приближения */
  background: transparent;
  padding: 6px 10px;
  border-radius: 15px;
  border: none;
}

.energy-bar {
  width: 100%;
  height: 6px;
  background: rgba(128, 128, 128, 0.5);
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(80, 80, 80, 0.2);
}

.energy-fill {
  height: 100%;
  background: linear-gradient(90deg, #f39c12, #e67e22); /* Orange-yellow gradient */
  border-radius: 6px;
  transition: width 0.3s ease;
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.6);
  animation: energyPulse 2s ease-in-out infinite alternate;
}

.upgrade-buttons {
  display: flex;
  justify-content: center;
  gap: 10px; /* Увеличенный зазор */
  background: transparent;
  padding: 12px; /* Увеличено на 2px */
  border-radius: 15px;
  border: none;
}

.upgrade-button {
  width: 85px; /* Увеличенный размер на 5px */
  height: 85px;
  background: #000000; /* Черный фон кнопок */
  border: 1px solid #808080;
  border-radius: 12px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.6);
  font-family: 'Inter', 'Roboto', sans-serif;
}

.upgrade-button:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.8);
  background: #404040;
  border-color: #a9a9a9;
}

.upgrade-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #000000;
  border-color: #696969;
}

.button-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px; /* Уменьшенный зазор */
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.upgrade-icon {
  width: 65px; /* Увеличенный размер на 5px */
  height: 65px;
  object-fit: contain;
  margin-bottom: 3px;
}

.button-level {
  font-size: 12px;
  font-weight: bold;
  margin-top: 2px;
}

.button-text {
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  margin-top: 2px;
}

.button-cost-container {
  display: flex;
  align-items: center;
  gap: 2px;
}

.coin-icon {
  width: 12px;
  height: 12px;
  object-fit: contain;
}

.button-cost {
  font-size: 9px;
  color: #cccccc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50px;
}

/* Responsive styles */
@keyframes energyPulse {
  0% {
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
  }
  100% {
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.7);
  }
}

@media (max-width: 480px) {
  .upgrade-window {
    padding: 10px;
    max-width: 305px; /* Увеличено на 5px */
  }
  
  .upgrade-button {
    width: 75px; /* Увеличено на 5px */
    height: 75px;
  }
  
  .upgrade-icon {
    width: 55px; /* Увеличено на 5px */
    height: 55px;
  }
  
  .button-cost {
    font-size: 8px;
    max-width: 40px;
  }
}
</style>